// @ts-check

import { emit } from '../../utils/event_bus.js?v=20260706d';
import { recordUndo } from '../../services/history_service.js?v=20260903c';
import {
  allDrawingObjects,
  applyDrawingObjectState,
  canonicalDrawingObjectKey,
  drawingObjectState,
  getDrawingObject,
  getDrawingObjectForOwner
} from '../../objects/drawing_object_registry.js?v=20260904f';
import { boundsCenter, transformSnapshotBounds } from '../geometry/bounds.js?v=20260706d';
import {
  resolveGroupSelection2D,
  resolveSelectionSet2D
} from '../selection/resolveGroupSelection.js?v=20260706d';
import {
  canRotateGroupWithAdapter,
  invokeGroupRotationAdapter,
  resolveGroupRotationAdapter,
  rotateDrawingObjectsWithDefaultAdapter
} from './rotationAdapters.js?v=20260904f';

function cleanId(value) {
  return String(value || '').trim();
}

function objectOwner(object = {}) {
  return cleanId(object?.systemType || object?.systemId || object?.owner);
}

function declaredOwners(value = {}) {
  return [...new Set([
    value?.systemType,
    value?.systemId,
    value?.systemKey,
    value?.system,
    value?.owner,
    value?.ownerId
  ].map(cleanId).filter(Boolean))];
}

function requestedOwner(options = {}) {
  const owners = declaredOwners(options);
  return owners.length <= 1 ? { valid: true, owner: owners[0] || '' } : { valid: false, owner: '' };
}

function canonicalObject(candidate, owner = '') {
  const id = cleanId(typeof candidate === 'string' ? candidate : candidate?.id);
  const candidateOwners = typeof candidate === 'string' ? [] : declaredOwners(candidate);
  const candidateOwner = candidateOwners[0] || '';
  if (!id || candidateOwners.length > 1 || (owner && candidateOwner && owner !== candidateOwner)) return null;
  return owner || candidateOwner
    ? getDrawingObjectForOwner(owner || candidateOwner, id)
    : getDrawingObject(id);
}

function canonicalObjects(candidates = [], owner = '') {
  const objectsByKey = new Map();
  for (const candidate of candidates || []) {
    const object = canonicalObject(candidate, owner);
    const key = object ? canonicalDrawingObjectKey(objectOwner(object), object.id) : '';
    if (key) objectsByKey.set(key, object);
  }
  return [...objectsByKey.values()];
}

function normalizeProvidedGroup(group = {}) {
  const objects = canonicalObjects(group?.objects || []);
  if (!objects.length) return null;
  const owners = [...new Set(objects.map(objectOwner).filter(Boolean))];
  if (!owners.length) return null;
  const claimedOwners = declaredOwners(group);
  if (claimedOwners.length > 1) return null;
  const claimedOwner = claimedOwners[0] || '';
  if (owners.length === 1 && claimedOwner && claimedOwner !== owners[0]) return null;
  if (owners.length > 1 && claimedOwner && claimedOwner !== 'core') return null;
  const primaryObject = canonicalObject(group.primaryObject) || objects[0];
  return {
    ...group,
    objects,
    primaryObject: objects.includes(primaryObject) ? primaryObject : objects[0],
    systemType: owners.length === 1 ? owners[0] : 'core'
  };
}

function groupForObjects(objects = [], options = {}) {
  const normalized = canonicalObjects(objects);
  if (!normalized.length) return null;
  return normalizeProvidedGroup({
    groupId: cleanId(options.groupId) || normalized.map(object => object.id).join('|'),
    objects: normalized,
    primaryObject: normalized[0],
    systemType: [...new Set(normalized.map(objectOwner))].length === 1
      ? objectOwner(normalized[0])
      : 'core',
    explicitGroup: options.explicitGroup === true
  });
}

function selectedOrProvidedGroup(options = {}) {
  if (options.group?.objects?.length) return normalizeProvidedGroup(options.group);
  if (options.groupId) {
    const requested = requestedOwner(options);
    if (!requested.valid) return null;
    const object = requested.owner
      ? getDrawingObjectForOwner(requested.owner, options.groupId)
      : getDrawingObject(options.groupId);
    if (object) return resolveGroupSelection2D(object);
    const members = allDrawingObjects()
      .filter(candidate => {
        if (requested.owner && objectOwner(candidate) !== requested.owner) return false;
        const metadata = candidate?.metadata || {};
        return [
          metadata.connectedGroupId,
          metadata.parentGroupId,
          metadata.groupId,
          metadata.sourceGroupId
        ].some(value => String(value || '').trim() === String(options.groupId || '').trim());
      });
    if (members.length) {
      const owners = new Set(members.map(objectOwner).filter(Boolean));
      if (owners.size !== 1) return null;
      return groupForObjects(members, { groupId: options.groupId, explicitGroup: true });
    }
    return requested.owner ? null : resolveGroupSelection2D(options.groupId);
  }
  if (Array.isArray(options.objects)) {
    const objects = canonicalObjects(options.objects);
    if (options.allowSelectionSet === true && objects.length > 1) {
      return groupForObjects(objects);
    }
    return resolveGroupSelection2D(objects);
  }
  const resolved = resolveGroupSelection2D();
  return resolved ? normalizeProvidedGroup(resolved) : null;
}

function groupSnapshot(group) {
  const snapshot = [];
  for (const object of group?.objects || []) {
    try {
      const state = drawingObjectState(object);
      if (!state) continue;
      snapshot.push({
        id: object.id,
        systemType: object.systemType,
        key: canonicalDrawingObjectKey(object.systemType, object.id),
        state
      });
    } catch (error) {
      console.warn(`DrawingObject-Rotationssnapshot für ${objectOwner(object) || '(ohne Owner)'} fehlgeschlagen.`, error);
    }
  }
  return snapshot;
}

function ownerGroups(group = {}) {
  const objectsByOwner = new Map();
  for (const object of group.objects || []) {
    const owner = objectOwner(object);
    if (!owner) continue;
    if (!objectsByOwner.has(owner)) objectsByOwner.set(owner, []);
    objectsByOwner.get(owner).push(object);
  }
  return [...objectsByOwner.entries()].map(([owner, objects]) => ({
    ...group,
    objects,
    primaryObject: objects.includes(group.primaryObject) ? group.primaryObject : objects[0],
    systemType: owner,
    systemId: owner,
    systemKey: owner,
    system: owner,
    owner,
    ownerId: owner
  }));
}

function restoreGroupSnapshot(snapshot = []) {
  let restored = 0;
  for (const entry of snapshot) {
    try {
      const object = getDrawingObjectForOwner(entry.systemType, entry.id);
      if (object && entry.state && applyDrawingObjectState(object, entry.state)) restored += 1;
    } catch (error) {
      console.warn(`DrawingObject-Rotationsrollback für ${entry.systemType || '(ohne Owner)'} fehlgeschlagen.`, error);
    }
  }
  return restored;
}

function ownerBoundRotationResult(result, group) {
  const allowedObjects = new Set(group.objects || []);
  const owner = objectOwner(group.primaryObject);
  return (Array.isArray(result) ? result : []).filter(entry => (
    allowedObjects.has(entry?.object)
    && objectOwner(entry.object) === owner
  ));
}

export function groupRotationPivot2D(group, options = {}) {
  if (Number.isFinite(Number(options.pivot?.x)) && Number.isFinite(Number(options.pivot?.z))) {
    return { x: Number(options.pivot.x), z: Number(options.pivot.z) };
  }
  const snapshot = groupSnapshot(group);
  return boundsCenter(transformSnapshotBounds(snapshot));
}

export function rotateGroup2D(options = {}) {
  const group = selectedOrProvidedGroup(options);
  if (!group?.objects?.length) return 0;
  const angleDeg = Number(options.angleDeg ?? 90) || 0;
  if (!angleDeg) return 0;
  const pivot = groupRotationPivot2D(group, options);
  if (!pivot && group.objects.length > 1) return 0;
  if (group.objects.some(object => object?.rotatable === false)) return 0;
  const state = options.state || null;
  const snapshot = groupSnapshot(group);
  const rotations = [];
  for (const ownerGroup of ownerGroups(group)) {
    const ownerSnapshot = snapshot.filter(entry => entry.systemType === ownerGroup.systemType);
    if (ownerSnapshot.length !== ownerGroup.objects.length) continue;
    const resolution = resolveGroupRotationAdapter(ownerGroup, state);
    if (!resolution.valid) {
      if (resolution.hookFailed) restoreGroupSnapshot(ownerSnapshot);
      continue;
    }
    if (!canRotateGroupWithAdapter(ownerGroup, state, resolution)) {
      if (resolution.hookFailed) restoreGroupSnapshot(ownerSnapshot);
      continue;
    }
    rotations.push({ ownerGroup, ownerSnapshot, resolution });
  }
  if (!rotations.length) return 0;
  if (options.recordHistory === true) {
    recordUndo(options.historyLabel || 'Gruppe drehen');
  }
  const rotated = [];
  for (const { ownerGroup, ownerSnapshot, resolution } of rotations) {
    try {
      let result;
      if (resolution.adapter) {
        const outcome = invokeGroupRotationAdapter(
          resolution,
          ownerGroup,
          angleDeg,
          pivot,
          state,
          options
        );
        if (!outcome.ok) {
          restoreGroupSnapshot(ownerSnapshot);
          continue;
        }
        result = outcome.result;
      } else {
        result = rotateDrawingObjectsWithDefaultAdapter(ownerGroup, angleDeg, pivot, {
            absolute: options.absolute === true,
            exactTransform: options.exactTransform,
            forcePivot: options.forcePivot === true,
            baseStates: options.baseStates
          });
      }
      rotated.push(...ownerBoundRotationResult(result, ownerGroup));
    } catch (error) {
      restoreGroupSnapshot(ownerSnapshot);
      console.warn(`2D-Gruppenrotation für ${ownerGroup.systemType} fehlgeschlagen.`, error);
    }
  }
  if (rotated.length) {
    emit('object:rotated', {
      objects: rotated.map(entry => entry.object).filter(Boolean),
      groupId: group.groupId,
      angleDeg,
      pivot,
      source: options.source || 'rotate-group-2d',
      absolute: Boolean(options.absolute)
    });
  }
  return rotated.length;
}

export function rotateDrawingSelection2D(angleDeg = 90, options = {}) {
  const objects = resolveSelectionSet2D(options.objects || null);
  return rotateGroup2D({
    ...options,
    objects,
    angleDeg,
    allowSelectionSet: options.allowSelectionSet === true
  });
}
